import React, { useState, useCallback } from 'react'
import {
  Col,
  Form,
  FormGroup,
  FormText,
  FormFeedback,
  Label,
  Input,
} from 'reactstrap'
import { emailPatternForHtml } from 'lib/util'

interface AssignAdminProps {
  defaultAdminEmail?: string
  onSelect: (data: { email: string; confirmEmail: string }) => void
}

const AssignAdmin: React.FC<AssignAdminProps> = ({
  defaultAdminEmail,
  onSelect,
}) => {
  const [formName] = useState('assign-admin')
  const [iAmAdmin, setIAmAdmin] = useState(false)
  const [email, setEmail] = useState('')
  const [confirmEmail, setConfirmEmail] = useState('')
  const [validation, setValidation] = useState<Record<string, boolean>>({})

  const validateElement = useCallback((el: HTMLInputElement, show = true) => {
    if (!el || !el.name) {
      return true
    }
    const toValidate = el.name
    const valid = el.checkValidity()
    if (show) {
      setValidation(prev => ({ ...prev, [toValidate]: valid }))
    }
    return valid
  }, [])

  const getClassName = useCallback(
    (id: string) => {
      switch (validation[id]) {
        case true:
          return 'is-valid'
        case false:
          return 'is-invalid'
        default:
          return ''
      }
    },
    [validation],
  )

  const extractValue = useCallback((el: HTMLInputElement) => {
    if (!el) return null
    const inputType = el.type
    switch (inputType) {
      case 'checkbox':
      case 'radio':
        return el.checked
      default:
        return el.value
    }
  }, [])

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      validateElement(e.target)
    },
    [validateElement],
  )

  const handleChangeIAmAdmin = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const checked = e.target.checked
      const newEmail = checked ? defaultAdminEmail || '' : ''
      const newConfirmEmail = checked ? defaultAdminEmail || '' : ''

      setIAmAdmin(checked)
      setEmail(newEmail)
      setConfirmEmail(newConfirmEmail)

      if (checked) {
        // validate emails (after state set)
        setTimeout(() => {
          const emailEl = document.getElementsByName(
            'email',
          )[0] as HTMLInputElement
          if (emailEl) validateElement(emailEl)
          const confirmEmailEl = document.getElementsByName(
            'confirmEmail',
          )[0] as HTMLInputElement
          if (confirmEmailEl) validateElement(confirmEmailEl)
        }, 0)
      }

      onSelect({
        email: newEmail,
        confirmEmail: checked ? newEmail : newConfirmEmail,
      })
    },
    [defaultAdminEmail, onSelect, validateElement],
  )

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e || !e.target || (!e.target.readOnly && !e.target.name)) return
      const el = e.target
      const elementName = el.name
      const newValue = extractValue(el)

      if (elementName === 'email') {
        setEmail(newValue as string)
      } else if (elementName === 'confirmEmail') {
        setConfirmEmail(newValue as string)
      }

      if (elementName === 'email' || elementName === 'confirmEmail') {
        onSelect({
          email: elementName === 'email' ? (newValue as string) : email,
          confirmEmail:
            elementName === 'confirmEmail'
              ? (newValue as string)
              : confirmEmail,
        })
      }
    },
    [email, confirmEmail, extractValue, onSelect],
  )

  const labelWidth = 3
  const fieldWidth = 9

  return (
    <Form name={formName} noValidate>
      <p className="float-right">* Required Field</p>
      <h4>Assign License Administrator</h4>
      <FormText>
        Designate a person responsible for assigning the user(s) and additional
        License Administrators.
      </FormText>
      <hr />
      {defaultAdminEmail && (
        <FormGroup row>
          <Label sm={labelWidth}>{/* spacer */}</Label>
          <Label check sm={fieldWidth} style={{ paddingLeft: '2rem' }}>
            <Input
              checked={iAmAdmin}
              name="iAmAdmin"
              onChange={handleChangeIAmAdmin}
              type="checkbox"
            />{' '}
            I will be a License Administrator
          </Label>
        </FormGroup>
      )}
      <FormGroup row>
        <Label for="email" sm={labelWidth}>
          Email *
        </Label>
        <Col sm={fieldWidth}>
          <Input
            aria-required="true"
            className={getClassName('email')}
            disabled={iAmAdmin}
            name="email"
            onBlur={handleBlur}
            onChange={handleChange}
            pattern={emailPatternForHtml}
            placeholder="Email"
            required
            type="text"
            value={email || ''}
          />
          <FormFeedback tooltip>Enter a valid Email Address.</FormFeedback>
        </Col>
      </FormGroup>
      {!iAmAdmin && (
        <FormGroup row>
          <Label for="email" sm={labelWidth}>
            Confirm Email *
          </Label>
          <Col sm={fieldWidth}>
            <Input
              aria-required="true"
              className={getClassName('confirmEmail')}
              disabled={iAmAdmin}
              name="confirmEmail"
              onBlur={handleBlur}
              onChange={handleChange}
              pattern={emailPatternForHtml}
              placeholder="Confirm Email"
              required
              type="text"
              value={confirmEmail || ''}
            />
            <FormFeedback tooltip>
              Confirmation Email is not valid or does not match.
            </FormFeedback>
          </Col>
        </FormGroup>
      )}
    </Form>
  )
}

export default AssignAdmin
